import React from 'react'
import { Observable } from 'rxjs'
import { AjaxResponse } from 'rxjs/ajax'
import { FinanceApiResponse } from '../types/finance'

interface Options<T> {
  immediate?: boolean
  initialData?: T
  onSuccess?: (_data: T | undefined, _response: FinanceApiResponse<T>) => void
  onError?: (_message: string) => void
}

const getErrorMessage = (error: any) => {
  return (
    error?.response?.error ||
    error?.response?.error_code ||
    error?.message ||
    'Something went wrong'
  )
}

export const useFinanceObservable = <T>(
  factory: () => Observable<AjaxResponse<FinanceApiResponse<T>>>,
  options: Options<T> = {},
) => {
  const { immediate = true, initialData, onSuccess, onError } = options
  const [data, setData] = React.useState<T | undefined>(initialData)
  const [loading, setLoading] = React.useState(immediate)
  const [error, setError] = React.useState<string | null>(null)

  const factoryRef = React.useRef(factory)
  const onSuccessRef = React.useRef(onSuccess)
  const onErrorRef = React.useRef(onError)

  React.useEffect(() => {
    factoryRef.current = factory
  }, [factory])

  React.useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  React.useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const run = React.useCallback(() => {
    setLoading(true)
    setError(null)
    const subscription = factoryRef.current().subscribe({
      next: (response) => {
        const payload = response.response || {}
        const nextData = payload.data
        setData(nextData)
        onSuccessRef.current?.(nextData, payload)
        if (payload.error) {
          setError(payload.error)
          onErrorRef.current?.(payload.error)
        }
      },
      error: (err) => {
        const message = getErrorMessage(err)
        setError(message)
        onErrorRef.current?.(message)
        setLoading(false)
      },
      complete: () => {
        setLoading(false)
      },
    })

    return subscription
  }, [])

  React.useEffect(() => {
    if (!immediate) {
      setLoading(false)
      return undefined
    }

    const subscription = run()
    return () => subscription?.unsubscribe()
  }, [immediate, run])

  return {
    data,
    loading,
    error,
    refresh: run,
    setData,
  }
}
